/*
 * Why a visit was cancelled or skipped (owner, 2026-11-04: 17 of 76 Cleaners visits in October were cancelled and nobody
 * knew why). Every cancel or skip saves a reason from this short list, who asked for it, and how many hours before the
 * visit it came. The office picks the reason with one tap; skipping the question saves "not given" and never blocks.
 */
import { z } from "zod";

export const CANCEL_REASONS = [
  "client_skip",
  "client_away",
  "weather",
  "no_crew",
  "unhappy",
  "price",
  "moved",
  "other",
  "not_given",
] as const;
export type CancelReason = (typeof CANCEL_REASONS)[number];

export const CANCEL_REASON_WORDS: Record<CancelReason, string> = {
  client_skip: "Client asked to skip",
  client_away: "Client sick or away",
  weather: "Weather",
  no_crew: "We had no cleaner or crew",
  unhappy: "Client unhappy",
  price: "Price",
  moved: "Moved or sold",
  other: "Other",
  not_given: "Not given",
};

/** Who asked for the cancel. Null when nobody said. */
export const CANCEL_BY = ["client", "us"] as const;
export type CancelBy = (typeof CANCEL_BY)[number];
export const CANCEL_BY_WORDS: Record<CancelBy, string> = { client: "Client", us: "Us" };

/** Under this many hours before the visit is a late cancel (every brand's terms say 24 hours, settings.ts). */
export const LATE_CANCEL_HOURS = 24;

/** When a visit has no start time, its hours count from 9 in the morning. */
export const NO_TIME_START = "09:00";

/** The usual answer for who asked, from the reason. Weather, other and not given could be either, so null. */
export function cancelByFor(reason: CancelReason): CancelBy | null {
  if (reason === "no_crew") return "us";
  if (reason === "weather" || reason === "other" || reason === "not_given") return null;
  return "client";
}

/** A late cancel: under 24 hours before the visit, or after it was due. Unknown hours are not late. */
export function isLateCancel(hours: number | null | undefined): boolean {
  return hours != null && hours < LATE_CANCEL_HOURS;
}

/**
 * Whole hours from when it was cancelled to when the visit was due to start (negative once the start time had passed).
 * visitStartUtc is the visit's start as a real moment (the server works it out in the company's time zone).
 */
export function hoursBefore(visitStartUtc: Date, cancelledAt: Date): number {
  const h = (visitStartUtc.getTime() - cancelledAt.getTime()) / 3_600_000;
  return h >= 0 ? Math.floor(h) : -Math.floor(-h);
}

/**
 * A best guess at the reason from words the client or the office wrote (old cancels, before the reason was asked).
 * Only plain, clear words count; anything else is null and the cancel stays "not given".
 */
export function guessCancelReason(text: string | null | undefined): CancelReason | null {
  const t = (text ?? "").toLowerCase();
  if (!t.trim()) return null;
  const rules: [CancelReason, RegExp][] = [
    ["moved", /\b(moving|moved|move out|sold (the|my|our) (house|home|place)|selling (the|my|our) (house|home))\b/],
    ["unhappy", /\b(unhappy|not happy|never cleaned|unacceptable|disappointed|terrible|awful|ridiculous|livid|complain)/],
    ["price", /\b(too expensive|expensive|afford|price went|raised (the|your) price|cheaper|budget)\b/],
    ["no_crew", /\b(no cleaner|no crew|cleaner (is )?sick|short.?staffed|nobody (came|showed)|no one (came|showed))\b/],
    ["weather", /\b(weather|rain|storm|hurricane|flood|freez)/],
    ["client_away", /\b(sick|ill\b|covid|flu|out of town|travel|traveling|vacation|away|hospital|surgery)/],
    ["client_skip", /\b(skip|not this (week|time)|next time|reschedule|postpone)\b/],
  ];
  for (const [reason, re] of rules) if (re.test(t)) return reason;
  return null;
}

/** Sent with Cancel or Skip. Every part is optional: no reason saves "not given". */
export const cancelInfoBody = z.object({
  reason: z.enum(CANCEL_REASONS).optional(),
  /** For "other" mostly; a blank note is no note. */
  note: z
    .string()
    .trim()
    .max(500)
    .optional()
    .transform((v) => (v === "" ? undefined : v)),
  by: z.enum(CANCEL_BY).nullable().optional(),
  /** A late cancel fee the office says applies, whole cents. Only noted here; nothing is charged. */
  feeCents: z.number().int().min(0).max(100_000).nullable().optional(),
});
export type CancelInfoBody = z.infer<typeof cancelInfoBody>;

/** How a cancelled or skipped visit shows its reason. */
export const cancelInfo = z.object({
  reason: z.enum(CANCEL_REASONS),
  note: z.string().nullable(),
  by: z.enum(CANCEL_BY).nullable(),
  /** Whole hours before the visit (negative after it was due); null for old cancels with no time kept. */
  hoursBefore: z.number().nullable(),
  late: z.boolean(),
  feeCents: z.number().nullable(),
  at: z.string().nullable(),
  /** Who in the office saved it, when known. */
  savedBy: z.string().nullable(),
});
export type CancelInfo = z.infer<typeof cancelInfo>;

// The Cancels view (Insights) ---------------------------------------------------------------------------------------------

const countRow = { off: z.number(), late: z.number() };
export const cancelsAnswer = z.object({
  from: z.string(),
  to: z.string(),
  /** Every visit in the days: done, cancelled and skipped, and what share were off. */
  totals: z.object({
    done: z.number(),
    cancelled: z.number(),
    skipped: z.number(),
    late: z.number(),
    lateWithFee: z.number(),
    feeCents: z.number(),
    /** Off visits (cancelled plus skipped) out of done plus off, whole percent. */
    rate: z.number(),
  }),
  months: z.array(
    z.object({
      month: z.string(),
      done: z.number(),
      cancelled: z.number(),
      skipped: z.number(),
      late: z.number(),
      rate: z.number(),
    }),
  ),
  reasons: z.array(z.object({ reason: z.enum(CANCEL_REASONS), ...countRow })),
  by: z.array(z.object({ by: z.enum(["client", "us", "unknown"]), ...countRow })),
  /** Clients with the most cancels and skips in the days, most first. */
  clients: z.array(
    z.object({ clientId: z.uuid(), name: z.string(), done: z.number(), ...countRow, lastReason: z.enum(CANCEL_REASONS) }),
  ),
  /** Every cancel and skip in the days, newest first, so a missing reason can be filled in. */
  list: z.array(
    z.object({
      ref: z.string(),
      date: z.string(),
      clientId: z.uuid(),
      clientName: z.string(),
      jobName: z.string(),
      status: z.enum(["cancelled", "skipped"]),
      info: cancelInfo,
    }),
  ),
});
export type CancelsAnswer = z.infer<typeof cancelsAnswer>;
